/**
 * emission — CPU-side rate accumulator + burst scheduler shared by the GPU
 * emitter. Mirrors the EXACT semantics of the CPU `ParticleEmitter` so the two
 * backends emit identical batch sizes for the same config + dt sequence
 * (verified by the parity unit test).
 *
 * The CPU emitter's continuous-emit logic is:
 *   accumulator += rate * dt
 *   toEmit = floor(accumulator)
 *   accumulator -= toEmit
 *
 * and bursts fire when `elapsed >= nextTime` with cycle/interval bookkeeping.
 */

import type { BurstConfig } from '../types.js';

/** A drained continuous-emission accumulator. */
export class RateAccumulator {
  private _acc = 0;

  /** Reset to empty (used by `stop()`). */
  reset(): void { this._acc = 0; }

  /** Current fractional remainder (exposed for tests / introspection). */
  get remainder(): number { return this._acc; }

  /**
   * Advance by `dt` at `rate` particles/sec and return the integer count to
   * spawn this tick, retaining the fractional remainder for the next tick.
   * A non-positive rate emits nothing and leaves the accumulator untouched.
   */
  tick(rate: number, dt: number): number {
    if (rate <= 0 || dt <= 0) return 0;
    this._acc += rate * dt;
    const toEmit = Math.floor(this._acc);
    this._acc -= toEmit;
    return toEmit;
  }
}

/** Internal mutable burst timer state, mirroring the CPU emitter's. */
interface BurstTimer {
  config: BurstConfig;
  nextTime: number;
  cyclesLeft: number; // -1 == infinite
}

/**
 * Burst scheduler. Tracks elapsed time and fires bursts on the same schedule
 * as the CPU emitter (`elapsed >= nextTime && cyclesLeft !== 0`).
 */
export class BurstScheduler {
  private timers: BurstTimer[] = [];
  private _elapsed = 0;

  constructor(bursts?: BurstConfig[]) {
    this.setBursts(bursts);
  }

  get elapsed(): number { return this._elapsed; }

  setBursts(bursts?: BurstConfig[]): void {
    this.timers = [];
    if (!bursts) return;
    for (const b of bursts) {
      this.timers.push({
        config: b,
        nextTime: b.time,
        cyclesLeft: b.cycles === 0 ? -1 : (b.cycles ?? 1),
      });
    }
  }

  /** Reset elapsed + all timers (used by `stop()`). */
  reset(): void {
    this._elapsed = 0;
    for (const t of this.timers) {
      t.nextTime = t.config.time;
      t.cyclesLeft = t.config.cycles === 0 ? -1 : (t.config.cycles ?? 1);
    }
  }

  /**
   * Advance elapsed time by `dt` and return the TOTAL number of particles to
   * spawn from all bursts that became due this tick. Mirrors the CPU emitter
   * (which advances `elapsed` once per frame, then checks every timer once).
   */
  tick(dt: number): number {
    this._elapsed += dt;
    let total = 0;
    for (const t of this.timers) {
      // A single tick fires each timer at most once, exactly like the CPU path.
      if (this._elapsed >= t.nextTime && t.cyclesLeft !== 0) {
        total += t.config.count;
        if (t.cyclesLeft > 0) t.cyclesLeft--;
        t.nextTime += t.config.interval || (1 / 60);
      }
    }
    return total;
  }
}
